/**
 * YTICS-1796: privacy-safe metadata about an edit to a previous response.
 *
 * We deliberately don't emit the raw candidate content to Segment (see
 * PR #3864 review) — candidate answers can contain PII (names, phone,
 * addresses, free-text personal info) and Segment forwards to Braze /
 * warehouse / other destinations. Length + delta + Levenshtein gives
 * analysts the signals from Lisa's YTICS-1796 spec ("did it change" +
 * "analyze the nature of the edit") without exposing content.
 */

export interface ResponseEditMetadata {
  responseChanged: boolean;
  previousResponseLength: number;
  updatedResponseLength: number;
  /** Naive length-delta — chars the response grew by. */
  charsAdded: number;
  /** Naive length-delta — chars the response shrank by. */
  charsRemoved: number;
  /**
   * Levenshtein edit distance. Captures the *actual* amount of rewriting
   * regardless of whether the overall length changed — e.g.
   * "Warehouse work" → "Cashier work" is `charsRemoved: 2` under the
   * naive delta but has an edit distance well above that once the
   * substituted characters are counted.
   */
  editDistance: number;
}

function levenshtein(a: string, b: string): number {
  if (a === b) {
    return 0;
  }
  if (a.length === 0) {
    return b.length;
  }
  if (b.length === 0) {
    return a.length;
  }
  // Two-row DP; keeps memory O(min(a,b)) instead of O(a*b). Locals
  // read out of `previous`/`current` because tsconfig's
  // noUncheckedIndexedAccess types every array index as T | undefined,
  // and the loop bounds already guarantee the indices are populated.
  const [shorter, longer] = a.length < b.length ? [a, b] : [b, a];
  let previous = Array.from({ length: shorter.length + 1 }, (_, i) => i);
  let current = new Array<number>(shorter.length + 1).fill(0);
  for (let i = 1; i <= longer.length; i++) {
    current[0] = i;
    for (let j = 1; j <= shorter.length; j++) {
      const cost = longer[i - 1] === shorter[j - 1] ? 0 : 1;
      const insertion = (current[j - 1] ?? 0) + 1;
      const deletion = (previous[j] ?? 0) + 1;
      const substitution = (previous[j - 1] ?? 0) + cost;
      current[j] = Math.min(insertion, deletion, substitution);
    }
    previous = current;
    current = new Array<number>(shorter.length + 1).fill(0);
  }
  return previous[shorter.length] ?? 0;
}

/**
 * Both inputs are expected trimmed by the caller (ChatContainer trims
 * before snapshotting to avoid trailing-whitespace false positives). A
 * `null` previous — the pre-edit path where isEditingPreviousResponse
 * is false — collapses to a "no prior response" baseline: everything in
 * `updated` counts as added.
 */
export function computeResponseEditMetadata(
  previous: string | null,
  updated: string,
): ResponseEditMetadata {
  const previousLength = previous?.length ?? 0;
  const updatedLength = updated.length;
  const responseChanged = (previous ?? "") !== updated;

  return {
    responseChanged,
    previousResponseLength: previousLength,
    updatedResponseLength: updatedLength,
    charsAdded: Math.max(0, updatedLength - previousLength),
    charsRemoved: Math.max(0, previousLength - updatedLength),
    editDistance: levenshtein(previous ?? "", updated),
  };
}
